import { Component, computed, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { WaitingListStore } from './waiting-list.store';
import { SubscriptionComponent } from '../../../../modules/shared/helpers/subscription.component';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import { SelectButtonComponent } from '../../../kit/select-button/select-button.component';
import { AppFormGroup } from '../../../../utils/form.utils';
import { fb, translate } from '../../../../modules/shared/shared.global-service';
import { SelectButtonOption } from '../../../kit/select-button/select-button.types';
import { SwitchInputComponent } from '../../../kit/input/switch-input/switch-input.component';
import { TableComponent } from '../../../../modules/shared/components/kit/table/table.component';
import { TableColumn, TableColumnType } from '../../../../modules/shared/components/kit/table/table.types';
import {
    boolColumn,
    clientChipField,
    dateField,
    dateToColumn,
    longTextField,
    numberField,
} from '../../../../modules/shared/components/kit/table/table.fields';
import { attendantsWaitingListFrag, clientsWaitingListFrag } from './waiting-list.query';
import { waitingListRemovalReasonOptions } from '@libs/types/src/services/enum/waiting-list-removal-reason.enum';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { PersonGridItemObject } from '@it2go/types';
import { personActiveAddress } from '../../../../utils/person.utils';
import {
    WaitingListAttendantUpsertDialogComponent,
} from '../waiting-list-attendant-upsert-dialog/waiting-list-attendant-upsert-dialog.component';
import { WaitingListObject } from '@libs/types/src/services/object-type/waiting-list.object';
import { FolderSocialStatusEnum } from '../../../../../../../types/src/services/enum/folder-status.enum';
import { CardTableComponent } from '../../../../modules/shared/components/kit/table/card-table.component';
import {
    WaitingListClientUpsertDialogComponent,
} from '../waiting-list-client-upsert-dialog/waiting-list-client-upsert-dialog.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import {
    DEFAULT_TAG_COLOR,
} from '../../setting/service-settings-offers-page/service-settings-offer-detail-page/service-settings-offer-detail-page.component';
import { NgStyle } from '@angular/common';
import { Chip } from '@openng/optimus-ui/chip';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { ColorContrastPipe } from '../../../../modules/shared/pipes/color-contrast.pipe';
import { formatPerson } from '@libs/shared/helpers/person.utils';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { TranslatePipe } from '@ngx-translate/core';
import { serviceFormEnumOptions } from '../../../../../../../types/src/services/enum/service-form.enum';
import { Operator } from '../../../../../../../data-grid/src/grid/grid-request.dto';
import { FormatOfferDurationPipe } from '../../../../modules/shared/pipes/format-service-offer-duration.pipe';

@Component({
    selector: 'app-waiting-list',
    templateUrl: './waiting-list.component.html',
    styleUrls: ['./waiting-list.component.scss'],
    providers: [WaitingListStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectButtonComponent,
        SwitchInputComponent,
        TableComponent,
        PrimeTemplate,
        Chip,
        Tooltip,
        NgStyle,
        ColorContrastPipe,
        TranslatePipe,
        FormatOfferDurationPipe,
    ],
})
export class WaitingListComponent extends SubscriptionComponent {

    public readonly store = inject(WaitingListStore);
    public readonly dialogService = inject(BaseDialogService);

    public attendantsTable = viewChild<CardTableComponent>('attendantsTable');
    public clientsTable = viewChild<CardTableComponent>('clientsTable');

    protected typeOptions = computed(() => {
        const ret = [];

        const useWaitingList = this.store.services().some((service) => {
            if (service.type !== ServiceTypeEnum.SOCIAL) return false;
            return service.social?.useWaitingList ?? false;
        });

        if (useWaitingList) {
            ret.push(<SelectButtonOption>{ value: 'attendants', key: 'attendants' });
        }
        ret.push(<SelectButtonOption>{ value: 'clients', key: 'clients' });

        return ret;
    });

    protected readonly prefix = 'waitingList.';

    protected filterForm = new AppFormGroup({
        type: fb.control<'clients' | 'attendants'>('clients'),
        active: fb.control(false),
    });
    protected typeChanged = this.filterForm.signalCodeChange<'clients' | 'attendants'>('type');
    protected activeChanged = this.filterForm.signalCodeChange<boolean>('active');

    protected additionalFilter = computed(() => {
        const type = this.typeChanged();

        const filter = new FilterBuilder();
        if (type.value === 'clients') {
            filter.where('folderStatus', FolderSocialStatusEnum.CLIENT);
            filter.where('dateFrom', true, Operator.NEMPTY);
        } else {
            filter.where('dateFrom', true, Operator.EMPTY);
        }

        if (!this.activeChanged().value) {
            filter.validByNow('dateFrom', 'dateTo');
        } else {
            filter.date(new Date(), 'dateTo', 'LT', false);
        }

        return filter.filter().filter;
    });

    protected readonly attendantsColumns = computed<TableColumn[]>(() => {
        return <TableColumn[]>[
            dateField('entryDate'),
            clientChipField('fullname', 'folder.person', true),
            {
                name: 'birthyear',
                type: TableColumnType.Year,
                dataKey: 'folder.person.birthdate',
            },
            {
                name: 'address',
                type: TableColumnType.MapLink,
                dataKey: 'folder.person',
                data: (person: PersonGridItemObject) => personActiveAddress(person),
                defaultHidden: true,
                filterable: true,
            },
            {
                name: 'form',
                filterable: {
                    type: 'multiSelect',
                    selectOptions: () => serviceFormEnumOptions.map((it) => {
                        return {
                            ...it,
                            name: translate.instant(it.name),
                        };
                    }),
                },
            },
            numberField('ratingPoints'),
            boolColumn('readyToUseService'),
            longTextField('note'),
            dateToColumn,
            {
                name: 'removalReason',
                type: TableColumnType.String,
                data: (item: any) => item ? translate.instant(`waitingList.enums.removalReason.${item}`) : '',
                filterable: {
                    type: 'select',
                    selectOptions: () => waitingListRemovalReasonOptions,
                },
            },
        ];
    });

    protected readonly clientsColumns = computed<TableColumn[]>(() => {
        return <TableColumn[]>[
            { ...dateField('onRecordFrom'), dataKey: 'dateFrom' },
            clientChipField('fullname', 'folder.person', true),
            {
                name: 'address',
                type: TableColumnType.MapLink,
                dataKey: 'folder.person',
                data: (person: PersonGridItemObject) => personActiveAddress(person),
                defaultHidden: true,
                filterable: true,
            },
            {
                name: 'offer',
                sortable: true,
                filterable: true,
            },
            { ...dateField('onRecordTo'), dataKey: 'dateTo' },
        ];
    });

    constructor() {
        super();

        this.store.onSuccess(
            () => {
                if (this.filterForm.value.type === 'clients') {
                    this.clientsTable()?.reload();
                } else if (this.filterForm.value.type === 'attendants') {
                    this.attendantsTable()?.reload();
                }
            },
            'createWaitingListItem',
            'updateWaitingListItem',
            'deleteWaitingListItem',
        );
    }

    protected openClientDetail(item: WaitingListObject | null = null) {
        const header = item ?
            translate.instant('waitingList.client.titleUpdate', { folder: formatPerson(item.folder.person) }) :
            translate.instant('waitingList.client.titleCreate');

        this.dialogService.open(WaitingListClientUpsertDialogComponent, {
            header,
            styleClass: 'p-dialog-width-50',
            data: { item },
        });
    }

    protected openAttendantDetail(item: WaitingListObject | null = null) {
        const header = item ?
            translate.instant('waitingList.attendant.titleUpdate', { folder: formatPerson(item.folder.person) }) :
            translate.instant('waitingList.attendant.titleCreate');

        this.dialogService.open(WaitingListAttendantUpsertDialogComponent, {
            header,
            styleClass: 'p-dialog-width-80',
            data: { item },
        });
    }

    protected readonly clientsWaitingListFrag = clientsWaitingListFrag;
    protected readonly attendantsWaitingListFrag = attendantsWaitingListFrag;
    protected readonly DEFAULT_TAG_COLOR = DEFAULT_TAG_COLOR;
}
